/**
 * Appointment Detail Modal
 * View and manage appointment details
 */

import { useState } from 'react';
import {
  useUpdateAppointmentStatus,
  useCancelAppointment,
  type AppointmentWithDetails,
} from '../../hooks/useAppointments';
import {
  formatDate,
  formatTime,
  formatDuration,
  getStatusColor,
  getStatusLabel,
} from '../../utils/calendar';
import type { AppointmentStatus } from '../../types';
import { AppointmentForm } from './AppointmentForm';

interface AppointmentDetailProps {
  appointment: AppointmentWithDetails;
  onClose: () => void;
  onRefresh: () => void;
}

export function AppointmentDetail({
  appointment,
  onClose,
  onRefresh,
}: AppointmentDetailProps) {
  const [showEditForm, setShowEditForm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const updateStatusMutation = useUpdateAppointmentStatus(appointment.id);
  const cancelMutation = useCancelAppointment();

  const statusColors = getStatusColor(appointment.status);
  const startTime = formatTime(appointment.appointment_datetime);
  const endDate = new Date(appointment.appointment_datetime);
  endDate.setMinutes(endDate.getMinutes() + appointment.duration_minutes);
  const endTime = formatTime(endDate);

  const handleStatusChange = async (status: AppointmentStatus) => {
    try {
      await updateStatusMutation.mutateAsync(status);
      onRefresh();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Villa kom upp við að uppfæra stöðu');
    }
  };

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync(appointment.id);
      setShowCancelConfirm(false);
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
      alert('Villa kom upp við að afbóka tíma');
    }
  };

  if (showEditForm) {
    return (
      <AppointmentForm
        appointment={appointment}
        onClose={() => setShowEditForm(false)}
        onSuccess={() => {
          setShowEditForm(false);
          onRefresh();
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Tímabókun</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Status Badge */}
          <div>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColors.bg} ${statusColors.text}`}
            >
              {getStatusLabel(appointment.status)}
            </span>
          </div>

          {/* Date & Time */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Dagsetning og tími</h3>
            <div className="text-lg font-semibold text-gray-900">
              {formatDate(appointment.appointment_datetime)}
            </div>
            <div className="text-base text-gray-700 mt-1">
              {startTime} - {endTime} ({formatDuration(appointment.duration_minutes)})
            </div>
          </div>

          {/* Patient */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Sjúklingur</h3>
            <div className="text-base font-semibold text-gray-900">
              {appointment.patient_name || 'Óþekktur sjúklingur'}
            </div>
          </div>

          {/* Provider */}
          {appointment.provider_name && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Meðferðaraðili</h3>
              <div className="text-base text-gray-900">{appointment.provider_name}</div>
            </div>
          )}

          {/* Appointment Type */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Tegund tíma</h3>
            <div className="text-base text-gray-900">{appointment.appointment_type}</div>
          </div>

          {/* Reason */}
          {appointment.reason && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Ástæða heimsóknar</h3>
              <div className="text-base text-gray-900">{appointment.reason}</div>
            </div>
          )}

          {/* Notes */}
          {appointment.notes && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Athugasemdir</h3>
              <div className="text-base text-gray-900 whitespace-pre-wrap">{appointment.notes}</div>
            </div>
          )}

          {/* Reminders */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Áminningar</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {appointment.reminder_sent_24h ? (
                  <svg
                    className="w-5 h-5 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                <span className="text-sm text-gray-700">24 tíma áminning</span>
              </div>
              <div className="flex items-center gap-2">
                {appointment.reminder_sent_2h ? (
                  <svg
                    className="w-5 h-5 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                <span className="text-sm text-gray-700">2 tíma áminning</span>
              </div>
            </div>
          </div>

          {/* Status Actions */}
          {appointment.status !== 'completed' &&
            appointment.status !== 'cancelled' &&
            appointment.status !== 'no_show' && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">Uppfæra stöðu</h3>
                <div className="flex flex-wrap gap-2">
                  {appointment.status === 'scheduled' && (
                    <button
                      onClick={() => handleStatusChange('confirmed')}
                      disabled={updateStatusMutation.isPending}
                      className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 transition-colors disabled:opacity-50"
                    >
                      Staðfesta
                    </button>
                  )}
                  {(appointment.status === 'scheduled' || appointment.status === 'confirmed') && (
                    <button
                      onClick={() => handleStatusChange('arrived')}
                      disabled={updateStatusMutation.isPending}
                      className="px-3 py-1.5 text-sm font-medium text-yellow-700 bg-yellow-100 rounded-md hover:bg-yellow-200 transition-colors disabled:opacity-50"
                    >
                      Mættur
                    </button>
                  )}
                  {appointment.status === 'arrived' && (
                    <button
                      onClick={() => handleStatusChange('in_progress')}
                      disabled={updateStatusMutation.isPending}
                      className="px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-100 rounded-md hover:bg-orange-200 transition-colors disabled:opacity-50"
                    >
                      Byrja meðferð
                    </button>
                  )}
                  {appointment.status === 'in_progress' && (
                    <button
                      onClick={() => handleStatusChange('completed')}
                      disabled={updateStatusMutation.isPending}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                      Ljúka
                    </button>
                  )}
                  {(appointment.status === 'scheduled' || appointment.status === 'confirmed') && (
                    <button
                      onClick={() => handleStatusChange('no_show')}
                      disabled={updateStatusMutation.isPending}
                      className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 transition-colors disabled:opacity-50"
                    >
                      Mætti ekki
                    </button>
                  )}
                </div>
              </div>
            )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <div>
            {appointment.status !== 'cancelled' && (
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
              >
                Afbóka
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Loka
            </button>
            {appointment.status !== 'completed' && appointment.status !== 'cancelled' && (
              <button
                onClick={() => setShowEditForm(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                Breyta
              </button>
            )}
          </div>
        </div>

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Staðfesta afbókun</h3>
              <p className="text-gray-700 mb-6">
                Ertu viss um að þú viljir afbóka þennan tíma? Þessa aðgerð er ekki hægt að afturkalla.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Hætta við
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {cancelMutation.isPending ? 'Afbókar...' : 'Afbóka'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
